import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Target, AlertTriangle } from "lucide-react";
import { api } from "../api";
import { PageHeader, GlassCard, ScoreRing, ProgressBar, Spinner, Chip } from "../components/ui";

const LISTS = [
  { key: "goals", label: "Credit Goals", tone: "blue", placeholder: "e.g. Pay off credit card by December" },
  { key: "collections", label: "Collections", tone: "red", placeholder: "e.g. ABC Collections - $420" },
  { key: "charge_offs", label: "Charge-Offs", tone: "red", placeholder: "e.g. XYZ Bank - $1,200" },
  { key: "late_payments", label: "Late Payments", tone: "amber", placeholder: "e.g. Auto loan - 30 days late" },
];

export default function PersonalCredit() {
  const [data, setData] = useState(null);
  const [inputs, setInputs] = useState({});

  const load = () => api.get("/personal").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const persist = async (patch) => {
    setData((d) => ({ ...d, ...patch }));
    try {
      const { data: res } = await api.put("/personal", patch);
      setData(res);
    } catch { toast.error("Could not save"); load(); }
  };

  const addItem = (key) => {
    const val = (inputs[key] || "").trim();
    if (!val) return;
    persist({ [key]: [...(data[key] || []), val] });
    setInputs((s) => ({ ...s, [key]: "" }));
  };
  const removeItem = (key, idx) => persist({ [key]: data[key].filter((_, i) => i !== idx) });

  if (!data) return <Spinner />;
  const negatives = (data.collections?.length || 0) + (data.charge_offs?.length || 0) + (data.late_payments?.length || 0);

  return (
    <div className="space-y-6">
      <PageHeader label="Personal" title="Personal Credit Builder"
        subtitle="Organize goals, track negatives, and monitor utilization to understand your personal credit health." />

      <div className="grid lg:grid-cols-3 gap-5">
        <GlassCard className="p-6 flex flex-col items-center justify-center text-center">
          <ScoreRing value={data.score} label="Health" size={150} />
          <p className="text-slate-400 text-sm mt-4">Educational credit-health estimate</p>
        </GlassCard>

        <GlassCard className="p-6 lg:col-span-2">
          <h3 className="cc-heading text-lg font-500 text-white mb-4">Credit Utilization</h3>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-slate-400">Current utilization</span>
            <span className="cc-heading text-2xl font-700 text-white">{data.utilization || 0}%</span>
          </div>
          <input type="range" min="0" max="100" value={data.utilization || 0}
            onChange={(e) => setData((d) => ({ ...d, utilization: Number(e.target.value) }))}
            onMouseUp={(e) => persist({ utilization: Number(e.target.value) })}
            onTouchEnd={(e) => persist({ utilization: Number(e.target.value) })}
            className="w-full accent-blue-500" data-testid="utilization-slider" />
          <div className="mt-4"><ProgressBar value={data.utilization || 0} /></div>
          <div className="mt-4 flex items-center gap-3 text-sm">
            {(data.utilization || 0) <= 30
              ? <Chip tone="green">Healthy — under 30% is ideal</Chip>
              : <Chip tone="amber"><AlertTriangle size={12} /> High — aim to lower below 30%</Chip>}
            <Chip tone={negatives === 0 ? "green" : "red"}>{negatives} negative item{negatives === 1 ? "" : "s"}</Chip>
          </div>
        </GlassCard>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {LISTS.map((list) => (
          <GlassCard key={list.key} className="p-6">
            <div className="flex items-center gap-2 mb-4">
              {list.key === "goals" ? <Target size={17} className="text-blue-300" /> : <AlertTriangle size={17} className="text-amber-300" />}
              <h3 className="cc-heading text-base font-500 text-white">{list.label}</h3>
              <Chip tone={list.tone}>{data[list.key]?.length || 0}</Chip>
            </div>
            <div className="space-y-2 mb-3">
              {(data[list.key] || []).length === 0 && <p className="text-sm text-slate-500">Nothing added yet.</p>}
              {(data[list.key] || []).map((item, i) => (
                <div key={i} className="flex items-center justify-between rounded-lg cc-surface border border-white/[0.05] px-3 py-2">
                  <span className="text-sm text-slate-200">{item}</span>
                  <button onClick={() => removeItem(list.key, i)} className="text-slate-500 hover:text-red-400"><X size={14} /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={inputs[list.key] || ""} onChange={(e) => setInputs((s) => ({ ...s, [list.key]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && addItem(list.key)} placeholder={list.placeholder}
                className="cc-input flex-1 rounded-lg px-3 py-2 text-sm" data-testid={`personal-input-${list.key}`} />
              <button onClick={() => addItem(list.key)} className="cc-btn-primary rounded-lg px-3 py-2" data-testid={`personal-add-${list.key}`}><Plus size={16} /></button>
            </div>
          </GlassCard>
        ))}
      </div>
    </div>
  );
}
